<template>
    <div :class="{'year-facet-group': group.isYearRange, 'is-collapsed': isCollapsed, 'is-collapsable': isCollapsable()}">

        <h3 class="title" @click="toggleFacets">{{ group.title }}</h3>

        <div v-if="false && group.isYearRange" class="range-facets">
            <label>
                From
                <input type="number"
                       :value="yearRange.min"
                       @blur="e => setYearRange({ range: 'min', value: e.target.value })">
            </label>

            <span></span>

            <label>To
                <input type="number"
                       :value="yearRange.max"
                       @blur="e => setYearRange({ range: 'max', value: e.target.value })">
            </label>
        </div>

        <ul :class="{'facets': true, 'year-facets': group.isYearRange}" v-if="!isCollapsed">
            <template v-for="facet in facets" :key="facet.id">
                <Facet v-if="!facet.isNested" :facet="facet"/>

                <li :class="`facet has-child ${childFacetIsVisible(facet.id) ? 'is-active' : ''}`" v-else>
                    <span class="facet--label" @click="toggleChild(facet.id)">{{ facet.label }}</span>

                    <ul class="facet--child" v-if="childFacetIsVisible(facet.id)">
                        <Facet v-for="f in facet.children" :key="f.id" :facet="f"/>
                    </ul>
                </li>
            </template>
        </ul>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import Facet from "./Facet";

export default {
    name: "SidebarFacetGroup",
    components: {Facet},

    props: ['group'],

    data() {
        return {
            isCollapsed: this.isCollapsedByDefault(),
            visibleChildren: [],
            windowHeight: 0,
            windowWidth: 0,
        };
    },

    mounted() {
        this.$nextTick(() => {
            this.onResize();
            window.addEventListener('resize', this.onResize);
        })
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },


    computed: {
        ...mapGetters({
            yearRange: 'search/yearRange',
        }),

        facets() {
            return this.group.groupedByLetterFacets ?? this.group.facets;
        },
    },

    methods: {
        ...mapActions({
            setYearRange: 'search/setYearRange'
        }),

        childFacetIsVisible(id) {
            return this.visibleChildren.indexOf(id) !== -1;
        },

        toggleChild(id) {
            const isVisible = this.visibleChildren.indexOf(id) > -1;

            if (isVisible) {
                this.visibleChildren = this.visibleChildren.filter(c => c !== id)
            } else {
                this.visibleChildren.push(id);
            }
        },

        toggleFacets() {
            if (!this.isCollapsable()) {
                return;
            }
            this.isCollapsed = !this.isCollapsed
        },

        onResize() {
            this.windowHeight = window.innerHeight
            this.windowWidth = window.innerWidth
        },

        isCollapsedByDefault() {
            if (this.isSmallScreen()) {
                return true;
            }

            return this.group.isYearRange;
        },

        isCollapsable() {
            return this.group.isYearRange || this.isSmallScreen();
        },

        isSmallScreen() {
            this.onResize();
            return this.windowWidth < 768;
        }
    }
}
</script>

<style lang="scss" scoped>

.title {
    text-transform: capitalize;
    position: relative;
}

.is-collapsable .title {
    cursor: pointer;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        top: 50%;
        border: 5px solid transparent;
        margin-top: -5px;
        border-top-color: #000;
        transition: all 0.3s ease-in-out;
    }
}

.is-collapsed .title {
    &:after {
        margin-top: -10px;
        border-top-color: transparent;
        border-bottom-color: #000;
    }
}

.facets {
    padding: 0 !important;
    margin: 0 !important;
    list-style: none;

    .mgt-spinner {
        position: relative;
        margin-top: 0;
        margin-right: .5rem;
    }

    ul {
        margin: 0;
        padding: 0;
        list-style: none;
    }
}


.facets:deep {
    .facet:not(.has-child) {
        display: flex;
        //flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;

        position: relative;
        cursor: pointer;
        margin-bottom: .3rem;
        padding: .1rem .3rem;

    }

    .facet:not(.is-active):not(.has-content):not(.has-child):hover {
        background: var(--mgt-facets-hover-bg-color);
        color: var(--mgt-facets-hover-color);
    }

    .has-child > .facet--label {
        &:after {
            content: "";
            width: 0;
            height: 0;
            border: 3px solid transparent;
            border-top-color: currentColor;
        }
    }


    .facet.has-child.is-active > .facet--label:after {
        border-top-color: transparent;
        border-bottom-color: currentColor;
    }

    .facet--remove {
        display: inline-block;
        flex-grow: 0;
        text-align: center;
        margin-right: .5rem;
        border-radius: .5rem;
        padding: .1rem .2rem;
        line-height: 1;
        border: 1px solid var(--mgt-facets-count-border-color);
        color: var(--mgt-facets-count-text-color);
        background-color: var(--mgt-facets-count-bg-color);
    }


    .facet--label {
        flex-grow: 2;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .has-child > & {
            font-weight: 500;
        }
    }

    .facet--count {
        line-height: 1;
        border-radius: .5rem;
        padding: .1rem .2rem;
        font-size: .8em;
        border: 1px solid var(--mgt-facets-count-border-color);
        color: var(--mgt-facets-count-text-color);
        background-color: var(--mgt-facets-count-bg-color);
    }

    .facet--child {
        position: relative;
        padding-left: 1rem;

        &:before {
            content: "";
            position: absolute;
            left: 0;
            width: 0;
            height: calc(100% - 1em);
            border-left: 1px dotted var(--mgt-facets-count-border-color);;
        }

        .facet {
            position: relative;
        }

        .facet:before {
            content: "";
            width: .8rem;
            height: 0;
            border-bottom: 1px dotted var(--mgt-facets-count-border-color);
            left: -1rem;
            position: absolute;
        }
    }
}

.range-facets {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;

    > label {
        width: calc(50% - 20px);
    }

    &:after {
        content: "";
        width: 50%;
        background: var(--mgt-primary-color);
        height: 1px;
        display: block;
        margin: 1rem auto;
    }

    > span {
        width: 10px;
        height: 2px;
        background: var(--mgt-primary-color);
        position: relative;
        top: -1rem;

        &:after {
            content: "";
            border: 0 solid var(--mgt-primary-color);
            border-width: 2px 2px 0 0;
            transform: rotate(45deg) translateY(calc(-50% - 2px));
            position: absolute;
            width: 6px;
            height: 6px;
            right: 3px;
            top: 50%;
        }
    }
}

.range-facets input {
    width: 100%;
    display: block;
    padding: .5rem 0 .5rem 1rem;
    border: 1px solid var(--mgt-primary-color);
}

</style>